import ApiService from './ApiService';

class PortalsService {
  static async getPortalBasicSettings(id, subportalId = 0) {
    const url = `/api3/portals/publication_config/?portal_id=${id}&subportal_id=${subportalId}`;
    const response = await ApiService.get(url);
    return response;
  }

  static async updatePortalBasicSettings(portalId, subportalId, config, value) {
    const url = `/api3/portals/publication_config/`;
    const data = {
      portal_id: portalId,
      subportal_id: subportalId,
      parameters: [
        {
          config,
          value,
        },
      ],
    };
    const response = await ApiService.post(url, data);
    return response;
  }

  static async getPortalConfigurations(portalId, subportalId) {
    let url = `/api3/portals/configurations/?portal_id=${portalId}`;

    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }

    const response = await ApiService.get(url);
    return response;
  }

  static async updatePortalConfigurations(params) {
    const url = '/api3/portals/configurations/';
    const response = await ApiService.post(url, params);
    return response;
  }

  static async getPortalRulesSummary(portalId, subportalId = 0) {
    const url = `/api3/portals/rules/summary/?portal_id=${portalId}&subportal_id=${subportalId}`;
    const response = await ApiService.get(url);
    return response;
  }

  static async getPortalStarringRules(portalId, subportalId, page = 0) {
    let url = `/api3/portals/rules/?portal_id=${portalId}&page=${page}`;

    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }

    const response = await ApiService.get(url);
    return response;
  }

  static async createRule(portalId, ruleData, subportalId) {
    let url = `/api3/portals/rules/?portal_id=${portalId}`;

    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }

    const response = await ApiService.post(url, ruleData);
    return response;
  }

  static async updateRule(ruleId, portalId, requestBody, subportalId) {
    let url = `/api3/portals/rules/${ruleId}/?portal_id=${portalId}`;
    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }
    const response = await ApiService.put(url, requestBody);
    return response;
  }

  static async patchRule(ruleId, portalId, requestBody, subportalId) {
    let url = `/api3/portals/rules/${ruleId}/?portal_id=${portalId}`;
    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }
    const response = await ApiService.patch(url, requestBody);
    return response;
  }

  static async getRule(ruleId, portalId, subportalId) {
    let url = `/api3/portals/rules/${ruleId}/?portal_id=${portalId}`;
    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }
    const response = await ApiService.get(url);
    return response;
  }

  static async deleteRule(ruleId, portalId, subportalId) {
    let url = `/api3/portals/rules/${ruleId}/?portal_id=${portalId}`;
    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }
    const response = await ApiService.delete(url);
    return response;
  }

  static async getProperties(ids) {
    const url = `/api3/property?id=${ids}`;
    const response = await ApiService.get(url);
    return response;
  }

  /** Same endpoint as legacy sender batch lookup; used by portals UI (e.g. XML sent properties). */
  static async getPropertiesById(listOfPropertiesId) {
    const url = `/api3/sender/property?ids=${listOfPropertiesId}`;
    const response = await ApiService.get(url);
    return response;
  }

  static async searchProperties(searchQuery, page = 1) {
    const url = `/api3/property`;
    const params = [];

    if (searchQuery) {
      params.push(`q=${encodeURIComponent(searchQuery)}`);
    }

    if (page) {
      params.push(`page=${page}`);
    }

    const finalUrl = params.length > 0 ? `${url}?${params.join('&')}` : url;
    const response = await ApiService.get(finalUrl);
    return response;
  }

  static async getDevelopments(ids) {
    const url = `/api3/sender/development?ids=${ids}`;
    const response = await ApiService.get(url);

    // Normalize various possible response shapes into a flat array
    const developments = Array.isArray(response)
      ? response
      : response?.developments || response?.results || [];

    return { developments };
  }

  static async getPropIdsMatchingCriteria(portalId, subportalId, requestBody) {
    const url = `/api3/portals/rules/criteria/search-publishables/?portal_id=${portalId}&subportal_id=${subportalId}&rule=${encodeURIComponent(
      JSON.stringify(requestBody)
    )}`;
    const response = await ApiService.get(url);
    return response;
  }

  static async updateOrder(idsArray, portalId, type, subportalId) {
    let url = `/api3/portals/rules/order/?portal_id=${portalId}`;
    if (subportalId) {
      url += `&subportal_id=${subportalId}`;
    }
    const data = {
      type,
      order: idsArray,
    };
    const response = await ApiService.put(url, data);
    return response;
  }

  static async getAttributesForCriteria(portalId, subportalId = 0) {
    const url = `/api3/portals/rules/criteria/property_search/filter/?portal_id=${portalId}&subportal_id=${subportalId}&criterion_rule=attributes&limit=10000&offset=0`;
    const response = await ApiService.get(url);
    return response;
  }

  static async getSelectOptions(valueApiPath) {
    // valueApiPath is the relative path from the attribute's value_api field
    const response = await ApiService.get(valueApiPath);
    return response;
  }

  static async getDevelopmentTypes() {
    const url = '/api3/development/types';
    const response = await ApiService.get(url);
    return response;
  }

  static async getCustomTagsSearch(params = {}) {
    const searchParams = new URLSearchParams();

    // Add search_name parameter if provided, in the future should be added more params
    if (params.search_name) {
      searchParams.append('search_name', params.search_name);
    }

    const queryString = searchParams.toString();
    const url = queryString
      ? `/api3/properties/custom_tags_search?${queryString}`
      : '/api3/properties/custom_tags_search';

    const response = await ApiService.get(url);
    return response;
  }

  static async requestSmartPublishingRulesUpgrade() {
    const url = '/api3/company/upgrade_pending';
    const requestBody = { smart_publishing_rules: true };
    const response = await ApiService.put(url, requestBody);
    return response;
  }

  static async getImportedFromOrigins() {
    const url = '/api3/portals/rules/criteria/imported_from/';
    const response = await ApiService.get(url);
    return response;
  }

  static async getXmlFiles(subportalId) {
    const subportal_id = Number(subportalId) || 0;
    const url = `/api3/simple-portal/xml-files/?subportal_id=${subportal_id}`;
    const response = await ApiService.get(url);
    if (Array.isArray(response)) {
      return response;
    }
    return response?.results ?? [];
  }

  static async addXmlFile(payload) {
    const url = '/api3/simple-portal/xml-files/';
    const response = await ApiService.post(url, payload);
    return response;
  }

  static async deleteXmlFile(fileId) {
    const url = `/api3/simple-portal/xml-files/${fileId}/`;
    const response = await ApiService.delete(url);
    return response;
  }

  static async getXmlFileHistory(subportalId, xmlFileId) {
    let url = `/api3/simple-portal/xml-files/history/?subportal_id=${subportalId}`;
    if (xmlFileId) url += `&xml_file_id=${xmlFileId}`;
    const response = await ApiService.get(url);
    return response;
  }
}

export default PortalsService;
